import React, { useEffect, useState } from 'react';
import { MenuItem, Grid } from '@mui/material';
import { RootState } from '../../state/store';
import {
  DistributionSelect,
  PriceTextFieldInput,
  SmallTypography,
  StyledInput,
} from '../../pages/ScenarioCreation/components/SegmentSelection/DynamicContents.Styled';
import { setDistributionSelected, setPriceInput } from './formSlice';
import CustomButton from '../UI/CustomButton/CustomButton';
import { useAppDispatch, useAppSelector } from '../../state/hooks';
import { formatToNumber } from '../../utils/helper';
import {
  fetchReviewTableDataThunk,
  toggleEnableReviewTableBtn,
} from '../../pages/ScenarioCreation/components/ReviewTable/reviewTableSlice';
import { hideLoader, showLoader } from '../../features/ui/loaderSlice';
import { openPopup } from '../../features/ui/popupSlice';
import { saveScenarioDraft } from '../../features/scenarioCreation/scenarioCreationSlice';
import AlertPopup from '../AlertPopup/AlertPopup';
import { SCENARIO_CREATION_DATA } from '../../utils/dataConstants';
import { InputButton, InputLine, InputTextNote, VerticalLine } from './FormStyles.Styled';

const FormInput: React.FC<{ selectedTab: any }> = ({ selectedTab }) => {
  const dispatch = useAppDispatch();
  const {
    current_price,
    current_distribution,
    competition_price,
    competition_distribution,
    distribution,
    ko_segment_distribution,
    ko_segment_ppl,
    comp_segment_distribution,
    comp_segment_ppl,
    min_price,
    max_price,
    current_tab,
    input_base_price
  } = useAppSelector((state: RootState) => state.inputKpi);

  const filtersState = useAppSelector((state) => state.filters);
  const { selectedSegment, selectedAvailablePack, selectedReplacedPack, distributionSelected, priceInput } =
    useAppSelector((state: RootState) => state.form);
  const { selectedBrands } = useAppSelector((state: RootState) => state.brand);
  const { mappings } = useAppSelector((state: RootState) => state.reviewTable);
  const scenarioName = useAppSelector((state: RootState) => state.scenarioCreation.scenarioName);
  const [localPriceInput, setLocalPriceInput] = useState(priceInput === 0 ? '' : priceInput.toString());
  const ko_constraints = SCENARIO_CREATION_DATA.INPUT_CONSTRAINTS_KO_SEGMENT;
  const ko_ppl_constraints = SCENARIO_CREATION_DATA.INPUT_CONSTRAINTS_KO_PPL;
  const current_constraints = SCENARIO_CREATION_DATA.INPUT_CONSTRAINTS_CURRENT;
  const comp_constraints = SCENARIO_CREATION_DATA.INPUT_CONSTRAINTS_COMPETITION;

  const handleAdd = async () => {
    const message = getDisabledMessage();
    if (!scenarioName) {
      dispatch(openPopup('Scenario name cannot be empty!'));
      return;
    }
    if (message === 'enable') {
      await dispatch(toggleEnableReviewTableBtn(true));
      await dispatch(showLoader());
      const payload = {
        bottler: filtersState.enabledKeys.bottler,
        state: filtersState.enabledKeys.state,
        category: filtersState.enabledKeys.category,
        pack_segment: [selectedSegment],
        pack_size: selectedAvailablePack !== null ? [selectedAvailablePack] : [],
        pack_size_replace: current_tab === 'Replace' ? [selectedReplacedPack] : [0],
        brand: selectedBrands,
        price: priceInput,
        distribution: distributionSelected,
        intervention_type: current_tab === 'Pricing & Distribution' ? 'Pricing' : current_tab,
      };
      const mappingsAfterUpdate = await dispatch(fetchReviewTableDataThunk({ ...payload, mappings: mappings }));

      await dispatch(
        saveScenarioDraft({ ...payload, scenario_name: scenarioName, mappings: mappingsAfterUpdate.payload }),
      );
      await dispatch(hideLoader());
    }
  };

  const maxValue = Math.round(max_price);
  const minValue = Math.round(min_price);

  useEffect(() => {
    setLocalPriceInput('');
    dispatch(setPriceInput(0));
    dispatch(setDistributionSelected(''));
  }, [current_tab, selectedSegment, selectedAvailablePack]);

  const getDisabledMessage = () => {
    if (!filtersState.enabledKeys.bottler) return dispatch(openPopup('Bottler is required.'));
    if (!filtersState.enabledKeys.state) return dispatch(openPopup('State is required.'));
    if (!filtersState.enabledKeys.category) return dispatch(openPopup('Category is required.'));
    if (!selectedSegment) return dispatch(openPopup('Segment is required.'));
    if (!selectedAvailablePack) return dispatch(openPopup('Pack Size is required.'));
    if (selectedBrands.length === 0) return dispatch(openPopup('At least one brand must be selected.'));
    if (priceInput <= 0) return dispatch(openPopup('Price must be greater than 0.'));
    if (!distributionSelected) return dispatch(openPopup('Distribution is required.'));
    return 'enable';
  };

  return (
    <div>
      <Grid style={{ display: 'flex' }}>
        <InputButton>Input</InputButton>
        <InputLine />
      </Grid>
      <Grid
        container
        spacing={2}
        style={{
          display: 'grid',
          gridTemplateColumns: '30% 30% 20% 20%',
          marginTop: '10px',
        }}
      >
        {selectedTab === 'Launch' ? (
          <>
            <Grid style={{ marginLeft: '17px' }}>
              {ko_constraints.map((label, index) => (
                <Grid item xs={6} key={index}>
                  <SmallTypography style={{ whiteSpace: 'nowrap', opacity: '59%' }}>{label}</SmallTypography>
                  <StyledInput>
                    {label === 'KO Segment PPL'
                      ? formatToNumber(ko_segment_ppl, 1)
                      : formatToNumber(ko_segment_distribution * 100, 1)}
                  </StyledInput>
                </Grid>
              ))}
            </Grid>
            <Grid>
              {ko_ppl_constraints.map((label, index) => (
                <Grid item xs={6} key={index}>
                  <SmallTypography style={{ whiteSpace: 'nowrap', opacity: '59%' }}>{label}</SmallTypography>
                  <StyledInput>
                    {label === 'Comp. Segment PPL'
                      ? formatToNumber(comp_segment_ppl, 1)
                      : formatToNumber(comp_segment_distribution * 100, 1)}
                  </StyledInput>
                </Grid>
              ))}
            </Grid>
          </>
        ) : (
          <>
            <Grid style={{ marginLeft: '17px' }}>
              {current_constraints.map((label, index) => (
                <Grid item xs={6} key={index}>
                  <SmallTypography style={{ whiteSpace: 'nowrap', opacity: '59%' }}>{ label === 'Current Price' ? `${label} (${formatToNumber(current_price, 1)})` : label}</SmallTypography>
                  <StyledInput>
                    {label === 'Current Price'
                      ? formatToNumber(input_base_price, 1)
                      : formatToNumber(current_distribution, 1)}
                  </StyledInput>
                </Grid>
              ))}
            </Grid>
            <Grid>
              {comp_constraints.map((label, index) => (
                <Grid item xs={6} key={index}>
                  <SmallTypography style={{ whiteSpace: 'nowrap', opacity: '59%' }}>{label}</SmallTypography>
                  <StyledInput>
                    {label === 'Comp. Segment Price'
                      ? formatToNumber(competition_price, 1)
                      : formatToNumber(competition_distribution, 1)}
                  </StyledInput>
                </Grid>
              ))}
            </Grid>
          </>
        )}
        <Grid style={{ display: 'flex' }}>
          <Grid>
            <VerticalLine />
          </Grid>
          <Grid style={{ marginLeft: '20px' }}>
            <Grid item xs={6}>
              <SmallTypography style={{ whiteSpace: 'nowrap', marginBottom: '10px' }}>
                Price{' '}
                <sup style={{ fontSize: '7.15px', fontWeight: 700 }}>
                  ({minValue}-{maxValue})
                </sup>
              </SmallTypography>
              <PriceTextFieldInput
                value={localPriceInput}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setLocalPriceInput(e.target.value);
                }}
                onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                  const parsedValue = parseFloat(localPriceInput);
                  if (!isNaN(parsedValue)) {
                    if (parsedValue < minValue) {
                      dispatch(setPriceInput(minValue));
                      setLocalPriceInput(minValue.toString());
                    } else if (parsedValue > maxValue) {
                      dispatch(setPriceInput(maxValue));
                      setLocalPriceInput(maxValue.toString());
                    } else {
                      dispatch(setPriceInput(parsedValue));
                    }
                  } else {
                    dispatch(setPriceInput(0));
                    setLocalPriceInput('');
                  }
                }}
                placeholder="--"
                fullWidth={true}
                type="number"
                inputProps={{
                  min: minValue,
                  max: maxValue,
                }}
              />
            </Grid>
            <Grid style={{ marginTop: '8px' }} item xs={6}>
              <SmallTypography>Distribution</SmallTypography>
              <DistributionSelect
                value={distributionSelected}
                onChange={(e) => dispatch(setDistributionSelected(e.target.value as string))}
                sx={{
                  '& .MuiSelect-select': {
                    padding: '0px !important',
                    background: '#F8F8F8 !important',
                    opacity: '70% !important',
                    fontSize: '12px',
                  },
                  '& .MuiOutlinedInput-input': {
                    paddingRight: '11px !important',
                  },
                }}
              >
                {distribution.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </DistributionSelect>
            </Grid>
          </Grid>
        </Grid>
        <Grid>
          <CustomButton
            fullWidth
            style={{
              marginTop: '10px',
              color: 'white',
              width: '86.18px',
              height: '36.5px',
              fontSize: '15.48px',
              lineHeight: '18.87px',
              fontWeight: 600,
            }}
            onClick={handleAdd}
          >
            Add
          </CustomButton>
        </Grid>
      </Grid>
      <Grid>
        <InputTextNote
          style={{
            marginTop: '20px',
          }}
        >
          Note 1 - All Value in new Launch are at Pack segment Level.
        </InputTextNote>
        <InputTextNote style={{}}>Note 2 - Base distribution is equal to 70% of segment distribution</InputTextNote>
      </Grid>
      <AlertPopup message={useAppSelector((state) => state.popup.message)} onConfirm={() => dispatch(openPopup(''))} />
    </div>
  );
};

export default FormInput;
